<script setup>
import { useArticleWeight } from '../composables/ArticleWeight.js'

const {
  isScaleReady,
  isScaleCalibration,
  isItemWeightCalibrated,
  scaleLastValueIntegerFormatted,
  scaleItemsCountTaken,
  scaleItemsCountTakenFormatted,
  scaleItemsCountFormatted,
  scaleLastValueFormatted,
  scaleInsertWeightFormatted,
  scaleItemWeightCalibrated,
  scaleItemUpdate,
  applyEmptyInsertWeight,
  applyScaleItemWeight,
  applyScaleItemsCount,
  applyScaleCalibration,
  cancelScaleCalibration,
} = useArticleWeight();

const {
  updateQuantity,
} = useArticleStock();
</script>

<template>
  <div class="w-full">
    <div style="margin: 0 -24px;">

      <!-- Weight scale calibration -->
      <div class="mt-8 px-[24px] pt-5 pb-6 border-t border-gray-200 bg-amber-100 dark:border-gray-600 dark:bg-gray-800/80" v-if="isScaleReady && !isScaleCalibration && isItemWeightCalibrated">
        <div class="flex flex-col gap-2">

          <div class="flex flex-row gap-4">
            <div>
              <span>Masa na tehtnici [g]</span>
              <div class="flex flex-row justify-stretch gap-0">
                <input type="text" class="w-full text-right font-medium tracking-widest
                  bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block p-2.5
                  dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  style="border-radius: 7px 0 0 7px;" v-model="scaleLastValueIntegerFormatted" :readonly="true" disabled />
                <Button type="warning" @click="tare()" style="border-radius: 0 7px 7px 0;">Tare</Button>
              </div>
            </div>
            <div v-if="isItemWeightCalibrated">
              <span v-if="scaleItemsCountTaken < 0">Št. dodanih kosov</span>
              <span v-else>Št. odvzetih kosov</span>
              <div class="flex flex-row justify-stretch gap-0">
                <input type="text" class="w-full flex-1 text-right font-medium tracking-widest
                  bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block p-2.5
                  dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  style="border-radius: 7px 0 0 7px;"
                  :class="{
                    '!text-green-600 dark:!text-green-400': scaleItemsCountTaken < 0,
                    '!text-amber-700 dark:!text-yellow-300': scaleItemsCountTaken > 0
                  }" v-model="scaleItemsCountTakenFormatted" placeholder="--" readonly disabled />
                  <div v-if="scaleItemsCountTaken < 0" class="text-green-600 dark:text-white dark:border-gray-500 border border-gray-300 bg-gray-50 outline-none font-medium rounded-lg text-sm !w-15 px-5 py-2.5 text-center dark:bg-blue-600" style="border-radius: 0 7px 7px 0; border-left-style: none; width: 57px;"><i class="fa fa-arrow-right-to-bracket rotate-90"></i></div>
                  <div v-else-if="scaleItemsCountTaken > 0" class="text-amber-700 dark:text-white dark:border-gray-500 border border-gray-300 bg-gray-50 outline-none font-medium rounded-lg text-sm !w-15 px-5 py-2.5 text-center dark:bg-blue-600" style="border-radius: 0 7px 7px 0; border-left-style: none; width: 57px;"><i class="fa fa-arrow-right-from-bracket -rotate-90"></i></div>
                  <div v-else class="relative text-gray-500 border border-gray-300 bg-gray-50 dark:text-gray-200 dark:border-gray-500 outline-none font-medium rounded-lg text-sm !w-15 px-4 py-2.5 text-center dark:bg-blue-600" style="border-radius: 0 7px 7px 0; border-left-style: none; width: 57px;"><svg version="1.1" viewBox="0 0 512 512" class="w-[14px] absolute top-[14px] left-[22px] fill-white -rotate-90" xmlns="http://www.w3.org/2000/svg">
                    <!--!Font Awesome Free v6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2026 Fonticons, Inc.-->
                    <path d="m160 96c17.7 0 32-14.3 32-32s-14.3-32-32-32h-64c-53 0-96 43-96 96v256c0 53 43 96 96 96h64c17.7 0 32-14.3 32-32s-14.3-32-32-32h-64c-17.7 0-32-14.3-32-32v-256c0-17.7 14.3-32 32-32z"/>
                  </svg></div>
              </div>
            </div>
            <div v-if="isItemWeightCalibrated">
              <span>Št. kosov na tehtnici</span>
              <div class="flex flex-row justify-stretch gap-0">
                <!-- <TextBox class="w-full text-right tracking-widest" v-model="scaleItemsCountFormatted" placeholder="--" :readonly="true" :enabled="scaleItemsCountFormatted !== null" /> -->
                <input type="text" class="w-full text-right font-medium tracking-widest
                  bg-white border border-gray-300 text-gray-900 text-sm rounded-lg block p-2.5
                  dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white"
                  style="border-radius: 7px 0 0 7px;" v-model="scaleItemsCountFormatted" placeholder="--" readonly disabled />
                <Button type="success" style="border-radius: 0 7px 7px 0;" @click="applyScaleItemsCount()"><i class="fa fa-check"></i></Button>
              </div>
            </div>
          </div>

        </div>

      </div>

      <!-- Weight scale calibration -->
      <div class="mt-8 px-4 pt-3 pb-4 border-t border-gray-200 bg-amber-100 dark:border-gray-600 dark:bg-gray-800/80" v-if="isScaleReady && isScaleCalibration">
        <div class="font-semibold mb-2">Tehtnica - umerjanje kosov</div>
        <div class="flex flex-col gap-2">
          
          <div>
            <span>Izmerjena masa [g]</span>
            <div class="flex flex-row justify-stretch gap-2">
              <TextBox class="w-full text-left font-mono font-medium tracking-wider" v-model="scaleLastValueFormatted" :readonly="true" :enabled="false" />
              <Button type="warning" @click="tare()">Tare</Button>
            </div>
          </div>

          <div>
            <span>Masa praznega vložka [g]</span>
            <div class="flex flex-row justify-stretch gap-2">
              <TextBox class="w-full text-left font-mono font-medium tracking-wider" v-model="scaleInsertWeightFormatted" />
              <Button type="warning" @click="applyEmptyInsertWeight()">Nastavi</Button>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <span>Masa kosa [g]</span>
              <TextBox class="w-full text-left font-mono font-medium tracking-wider" v-model="scaleItemWeightFormatted" :readonly="true" :enabled="false" />
              <!-- <div class="flex flex-row justify-stretch gap-2">
                <TextBox class="w-full" v-model="scaleItemWeightFormatted" :readonly="true" />
                <Button type="success" @click="applyScaleCalibration()"><i class="fa fa-check"></i></Button>
              </div> -->
            </div>
            <div>
              <span>Št. kosov v vložku</span>
              <div class="flex flex-row justify-stretch gap-2">
                <TextBox class="w-full text-left font-mono font-medium tracking-wider" v-model="scaleItemsUserCountFormatted" type="numeric" />
                <Button type="warning" @click="scaleItemUpdate(-1)"><i class="fa fa-arrow-down"></i></Button>
                <Button type="warning" @click="scaleItemUpdate(+1)"><i class="fa fa-arrow-up"></i></Button>
              </div>
            </div>
          </div>

          <div class="flex flex-row justify-between gap-2 mt-2">
            <div v-if="isItemWeightCalibrated" class="grid">
              <div v-if="canCalibrateWeightScale" class="grid place-items-center">
                <div class="w-full text-left">Shranjena masa kosa <Tag color="yellow">{{ scaleItemWeightCalibrated }}</Tag> g</div>
              </div>
            </div>
            <div v-else><!-- Dummy --></div>
            <div class="flex flex-row justify-end gap-2 mt-2">
              <Button type="danger" @click="cancelScaleCalibration()"><i class="fa fa-xmark mr-3"></i> Prekliči</Button>
              <Button type="success" @click="applyScaleCalibration()"><i class="fa fa-check mr-3"></i> Potrdi</Button>
            </div>
          </div>

        </div>

      </div>


    </div>



  </div>
</template>